import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import QRScanner from '../components/QRScanner';
import QRScanStats from '../components/QRScanStats';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import UserDetailsEditor from '../components/UserDetailsEditor';
import { Switch } from '../components/ui/switch';
import BadgePreviewModal from '../components/BadgePreviewModal';
import DynamicBadgeRenderer from '../components/DynamicBadgeRenderer';
import DynamicBadgePrintModal from '../components/DynamicBadgePrintModal';
import { previousScanMessage } from '../utils/scanHistory';
import PrintingInProgress from '../components/PrintingInProgress';
import { deskPath, badgeShareAllowed } from '../utils/printWindow';
import useDeskPrintReturn from '../hooks/useDeskPrintReturn';
import OfflineScanStatus from '../components/OfflineScanStatus';
import ScannerNameChip from '../components/ScannerNameChip';
import { useDebounce } from '../hooks/useDebounce';
import { useScanPageManifest } from '../hooks/useScanPageManifest';
import { clonePurposeConfig, isReaderBurst, pickSearchScanCode } from '../utils/scanPurpose';
import { formatDateTime } from '../utils/datetime';
import { toTitleCase } from '../utils/nameCase';
import { DEFAULT_PUBLIC_SEARCH_FIELDS, normalizeFieldKey, pickDisplayFields } from '../utils/formSchemaFields';
import { ArrowLeft, Printer, Save, RotateCcw, AlertTriangle, RefreshCcw, Download, Monitor, Smartphone, Eye, CheckCircle, Search, Mail, Phone } from 'lucide-react';
import { useEventRoute } from '../utils/eventRef';

const searchFieldIcon = (label) => {
  const key = normalizeFieldKey(label);
  if (key.includes('email')) return Mail;
  if (['contact', 'phone', 'mobile'].some((k) => key.includes(k))) return Phone;
  return null;
};

const QRScanFlowPage = () => {
  const { eventId, eventRef } = useEventRoute();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showSuccess, showError, showWarning } = useToast();
  
  const [scannedUser, setScannedUser] = useState(null);
  const [currentStep, setCurrentStep] = useState('scan'); // 'scan', 'edit', 'preview'
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentFormData, setCurrentFormData] = useState(null);
  const [showBadgePreviewModal, setShowBadgePreviewModal] = useState(false);
  const [selectedRegistrationForBadge, setSelectedRegistrationForBadge] = useState(null);
  const [showDynamicBadgePrint, setShowDynamicBadgePrint] = useState(false);
  const [badgePreviewData, setBadgePreviewData] = useState(null);
  const [isForceRefreshing, setIsForceRefreshing] = useState(false);
  const [continuousScanMode, setContinuousScanMode] = useState(false);
  
  // Debounced form data for live preview (400ms delay)
  const debouncedFormData = useDebounce(currentFormData, 400);

  // Extract purpose and registration ID query parameters
  const searchParams = new URLSearchParams(location.search);
  const purposeQueryParam = searchParams.get('purpose');
  const registrationIdParam = searchParams.get('registrationId') || searchParams.get('userId');
  const autoScanParam = searchParams.get('autoScan') === 'true';
  const showAllPurposes = searchParams.get('allPurposes') === 'true';
  // Opened from the event desk: return there once a badge has been printed.
  const fromDesk = searchParams.get('from') === 'desk';

  // Remember the purpose from the URL for this page visit. If a later navigation
  // drops ?purpose=, put it back so the scanner does not lose its selection.
  const lockedPurposeRef = useRef(purposeQueryParam);
  if (purposeQueryParam) lockedPurposeRef.current = purposeQueryParam;

  // State for auto-scanning when registrationId is provided
  const [isAutoScanning, setIsAutoScanning] = useState(false);
  const awaitingAutoScan = !!registrationIdParam && !scannedUser;
  const autoScannedIdRef = useRef(null);

  // Drop the one-off registrationId so the page returns to normal scanning and the auto-scan does not re-fire.
  const clearAutoScanParams = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has('registrationId') && !params.has('userId')) return;
    params.delete('registrationId');
    params.delete('userId');
    params.delete('autoScan');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };
  
  // State for external scanner input handling
  const [externalScannerInput, setExternalScannerInput] = useState('');

  // Manual search (typed, not scanned) — search attendees by name/email/contact/id
  const [manualSearch, setManualSearch] = useState('');
  const debouncedManualSearch = useDebounce(manualSearch, 400);
  const [searchScanResult, setSearchScanResult] = useState(null);
  const [isSearchScanning, setIsSearchScanning] = useState(false);
  const manualSearchInputRef = useRef(null);
  const keyTimesRef = useRef([]);
  const [scanMethod, setScanMethod] = useState('reader'); // 'reader' (keyboard/reader/search), 'camera', 'face'

  // Purpose is owned here so the search box and the scanner always act on the same purpose.
  const [selectedPurpose, setSelectedPurpose] = useState(purposeQueryParam);

  // Fetch event details (PUBLIC - no auth required for scan route)
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['eventPublic', eventId],
    queryFn: () => ApiService.getPublicEvent(eventId),
    enabled: !!eventId,
  });

  const { isPrintingBadge, handleBadgePrintStarted, backToDesk } = useDeskPrintReturn({
    fromDesk,
    eventId,
    showProgress: event?.showPrintProgress !== false,
  });

  // Fetch event form schema (also via public event getter)
  const { data: formSchema = [] } = useQuery({
    queryKey: ['eventFormSchemaPublic', eventId],
    queryFn: () => ApiService.getPublicEvent(eventId).then(event => event?.formSchema || []),
    enabled: !!eventId,
  });

  // Fetch dynamic badge configuration
  const { data: badgeConfig, isLoading: badgeConfigLoading, error: badgeConfigError, refetch: refetchBadgeConfig } = useQuery({
    queryKey: ['badgeDesignerConfig', eventId, scannedUser?.purposeConfig?.purpose || 'badge'],
    queryFn: async () => {
      const purpose = scannedUser?.purposeConfig?.purpose || 'badge';
      const config = await ApiService.getBadgeDesignerConfig(eventId, purpose);

      return config;
    },
    enabled: !!eventId,
    retry: 1,
    onError: (error) => {
      console.error('Failed to fetch badge config:', error);
    }
  });

  // Fetch QR scan purposes
  const { data: allPurposes = [] } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
    enabled: !!eventId,
  });

  // Check if purpose query parameter is valid
  const isValidPurpose = purposeQueryParam 
    ? allPurposes.some(purpose => purpose.purpose === purposeQueryParam)
    : true;

  // Filter purposes based on query parameter
  const filteredPurposes = purposeQueryParam && isValidPurpose && !showAllPurposes
    ? allPurposes.filter(purpose => purpose.purpose === purposeQueryParam)
    : allPurposes;
  const canToggleAllPurposes = !!purposeQueryParam && isValidPurpose && allPurposes.length > 1;

  const setShowAllPurposes = (checked) => {
    const params = new URLSearchParams(location.search);
    if (checked) params.set('allPurposes', 'true'); else params.delete('allPurposes');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  // Switching purpose reloads the page on the new ?purpose= so stats, badge config and scanner state start clean.
  const switchPurpose = (value) => {
    if (!value || value === selectedPurpose) return;
    const params = new URLSearchParams(location.search);
    params.set('purpose', value);
    // A page opened without ?purpose= offered every purpose; keep offering them after the reload.
    if (!purposeQueryParam || !isValidPurpose) params.set('allPurposes', 'true');
    ['registrationId', 'userId', 'autoScan'].forEach((key) => params.delete(key));
    window.location.assign(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    if (filteredPurposes.length > 0 && !filteredPurposes.some(p => p.purpose === selectedPurpose)) {
      setSelectedPurpose(filteredPurposes[0].purpose);
    }
  }, [filteredPurposes, selectedPurpose]);

  // The purpose applied to manual search, hardware scanner and camera scans.
  const targetPurpose = allPurposes.find(p => p.purpose === selectedPurpose)
    || (purposeQueryParam ? allPurposes.find(p => p.purpose === purposeQueryParam) : null)
    || allPurposes[0];

  const showManualSearch = event?.scanManualSearchEnabled !== false;
  const showQrInput = event?.scanQrInputEnabled !== false;
  const [scanLogoError, setScanLogoError] = useState(false);
  const scanBackground = event?.pageBackgrounds?.SCAN || null;
  // Scan-only logo first, then the registration page's form logo / event logo.
  const scanLogo = event?.scanShowLogo && !scanLogoError
    ? (event.scanLogoUrl || event.formLogoUrl || event.eventLogo || null)
    : null;

  useScanPageManifest(event?.name, eventId);

  // Manual-search results (same search API the registrations table uses).
  const { data: manualSearchResponse, isFetching: isManualSearching } = useQuery({
    queryKey: ['scanManualSearch', eventId, debouncedManualSearch],
    queryFn: () => ApiService.searchScanAttendees(eventId, {
      searchTerm: debouncedManualSearch.trim(),
      page: 0,
      size: 10,
      sortBy: 'createdAt',
      sortDirection: 'desc',
    }),
    enabled: !!eventId && showManualSearch && debouncedManualSearch.trim().length > 0,
  });
  const manualSearchResults = manualSearchResponse?.content || [];

  // Play error sound
  const playErrorSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 400;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  };

  // Fetch registration by ID mutation
  const fetchRegistrationMutation = useMutation({
    mutationFn: ({ userId }) => ApiService.getRegistration(eventId, userId),
    onSuccess: (registrationData, variables) => {
      const { scanResult } = variables;
    
      
      // Store the purpose config in the scannedUser object BEFORE setting it
      if (scanResult?.purposeConfig) {
        registrationData.purposeConfig = clonePurposeConfig(scanResult.purposeConfig);
      } else {
        console.log('❌ No purposeConfig found in scanResult');
      }
      
      setScannedUser(registrationData);
      setCurrentFormData(registrationData.formData || {});
      
      // Reset auto-scanning state on successful scan
      if (isAutoScanning) {
        setIsAutoScanning(false);
      }
      
      // Check purpose configuration from scan result
      const purposeConfig = scanResult?.purposeConfig;
      if (purposeConfig) {
        // Handle continuous scan mode (no edit, no badge)
        if (!purposeConfig.canUpdateUser && !purposeConfig.shouldPrintBadge) {
          setContinuousScanMode(true);
          showSuccess('Scan successful!');
          // Stay on scan step for continuous scanning
          return;
        }
        
        // Handle different combinations of canUpdateUser and shouldPrintBadge
        if (purposeConfig.shouldPrintBadge) {
          // Directly open the print badge modal
          setContinuousScanMode(false);
          setShowDynamicBadgePrint(true);
          // showSuccess('User data fetched successfully!');
        } else if (purposeConfig.canUpdateUser && !purposeConfig.shouldPrintBadge) {
          // Show only edit, no badge preview
          setContinuousScanMode(false);
          setCurrentStep('edit');
          // showSuccess('User data fetched successfully!');
        } else {
          // Neither edit nor badge preview
          setContinuousScanMode(false);
          setCurrentStep('preview');
          // showSuccess('User details verified successfully');
        }
        return;
      }

      // Legacy behavior (no purpose config)
      setContinuousScanMode(false);
      setCurrentStep('edit');
      showSuccess('User data fetched successfully!');
    },
    onError: (error, variables) => {
      console.error('Failed to fetch registration:', error);
      
      // Determine if we should be in continuous mode based on purpose config
      const { scanResult } = variables;
      const purposeConfig = scanResult?.purposeConfig;
      const shouldBeContinuousMode = purposeConfig && !purposeConfig.canUpdateUser && !purposeConfig.shouldPrintBadge;
      
      // Check if we're in continuous scan mode
      if (continuousScanMode || shouldBeContinuousMode) {
        // Show error on same page with sound
        playErrorSound();
        
        // Set continuous mode if not already set
        if (!continuousScanMode && shouldBeContinuousMode) {
          setContinuousScanMode(true);
        }
        // Error will be displayed in QRScanner component
      } else {
        showError('Invalid QR code or registration not found');
        setCurrentStep('scan');
        clearAutoScanParams();
      }
      
      // Reset auto-scanning state on error
      if (isAutoScanning) {
        setIsAutoScanning(false);
      }
    },
  });


  // Update user registration mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, formData }) => 
      ApiService.updateRegistration(eventId, userId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['eventRegistrations', eventId]);
      
      // Show appropriate success message based on purpose configuration
      if (scannedUser?.purposeConfig?.shouldPrintBadge) {
        showSuccess('User details updated successfully! Generating badge preview...');
      } else {
        showSuccess('User details updated successfully!');
      }
      
      setIsUpdating(false);
      setCurrentStep('preview');
      
      // Note: Badge preview will be updated automatically via debounced effect if shouldPrintBadge is true
    },
    onError: (error) => {
      showError('Failed to update user details. Please try again.');
      console.error('Update user error:', error);
      setIsUpdating(false);
    },
  });

  const handleQRScan = (scanResult) => {
 
   
    if (!scanResult) {
      console.error('❌ Invalid QR code format - scanResult is null/undefined');
      showError('Invalid QR code format');
      return;
    }

    // Handle backend errors (like "already scanned", "not found", etc.)
    if (scanResult.error || scanResult.scanStatus === 'error') {

      
      // Determine if we should be in continuous mode based on purpose config
      const purposeConfig = scanResult.purposeConfig;
      const shouldBeContinuousMode = purposeConfig && !purposeConfig.canUpdateUser && !purposeConfig.shouldPrintBadge;
      
      if (continuousScanMode || shouldBeContinuousMode) {
        // Show error on same page with sound for continuous mode
        playErrorSound();
        
        // Set continuous mode if not already set
        if (!continuousScanMode && shouldBeContinuousMode) {
          setContinuousScanMode(true);
        }
        // Error will be displayed in QRScanner component
      } else {
        showError(scanResult.message || 'An error occurred during scanning');
      }
      return;
    }

    // Repeat scans are allowed for this purpose; surface the history even when a modal takes over.
    const repeatMessage = previousScanMessage(scanResult);
    if (repeatMessage) showWarning(repeatMessage);

    // Check if we have a scan result with purpose configuration
    if (scanResult.purposeConfig) {
        
      if (scanResult.userId) {
        // Use the scanned userId to fetch registration data
        fetchRegistrationMutation.mutate({
          userId: scanResult.userId,
          shouldPrintBadge: scanResult.purposeConfig.shouldPrintBadge,
          scanResult // Pass the entire scan result to access purpose config in onSuccess
        });
      }
    } else if (typeof scanResult === 'string') {
      // Legacy format - direct registration ID
      fetchRegistrationMutation.mutate({
        userId: scanResult,
        shouldPrintBadge: true, // Default to true for backward compatibility
        scanResult: null
      });
    } else if (scanResult.userId) {

      // Legacy format - object with userId
      fetchRegistrationMutation.mutate({
        userId: scanResult.userId,
        shouldPrintBadge: true, // Default to true for backward compatibility
        scanResult: null
      });
    } else {
      console.error('❌ Invalid QR code format - no valid data found');
      showError('Invalid QR code format');
    }
  };

  // Admin-configured result fields; the name is already the card title.
  const searchResultFields = (event?.publicSearchFields ?? DEFAULT_PUBLIC_SEARCH_FIELDS)
    .filter((label) => !['name', 'fullname'].includes(normalizeFieldKey(label)));

  // Pull a display value out of formData by trying a few common label variants.
  const getFormField = (registration, keys) => {
    const formData = registration?.formData || {};
    const entries = Object.keys(formData);
    for (const key of keys) {
      const match = entries.find((fk) => fk.toLowerCase() === key.toLowerCase());
      if (match && formData[match] != null && formData[match] !== '') {
        return String(formData[match]);
      }
    }
    return null;
  };

  // Search pick or hardware-scanned code → record it via the scan API (same as the registrations
  // page Print Badge), then continue with the normal scan flow (print modal / edit / continuous).
  const scanFromSearch = async (code) => {
    const qrData = code?.trim();
    if (!qrData || isSearchScanning) return;
    if (!targetPurpose) {
      showError('No scan purpose configuration found for this event');
      return;
    }
    const purposeConfig = clonePurposeConfig(targetPurpose);
    setIsSearchScanning(true);
    setSearchScanResult(null);
    try {
      const result = await ApiService.scanQR(eventId, {
        qrData,
        purpose: purposeConfig.purpose,
        scanMode: 'manual',
        shouldPrintBadge: purposeConfig.shouldPrintBadge,
        canUpdateUser: purposeConfig.canUpdateUser,
      });
      if (result?.error) {
        throw new Error(result.message || result.error);
      }
      setSearchScanResult({
        ok: true,
        userId: result.userId,
        name: toTitleCase(getFormField(result, ['Name', 'name', 'Full Name', 'fullName'])),
        category: getFormField(result, ['Category', 'category']),
        message: `${purposeConfig.name || purposeConfig.purpose} recorded${result.offline ? ' (offline, will sync)' : ''}`,
        previousScans: previousScanMessage(result),
        at: new Date(),
      });
      handleQRScan({ ...result, scanStatus: 'success', purpose: purposeConfig.name, purposeConfig });
    } catch (error) {
      playErrorSound();
      setSearchScanResult({ ok: false, userId: qrData, message: error.message || 'Scan failed', at: new Date() });
    } finally {
      setIsSearchScanning(false);
      setManualSearch('');
      manualSearchInputRef.current?.focus();
    }
  };

  const handleManualSearchEnter = () => {
    const burst = isReaderBurst(keyTimesRef.current);
    keyTimesRef.current = [];
    // Reader bursts and reader-only mode scan the raw code; typed text may resolve to a search hit.
    const code = burst || !showManualSearch
      ? manualSearch.trim()
      : pickSearchScanCode(manualSearch, debouncedManualSearch, manualSearchResults);
    if (code) scanFromSearch(code);
  };

  const trackKeyTiming = (e) => {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    // A fresh code starts when the box is empty or after a pause.
    const last = keyTimesRef.current[keyTimesRef.current.length - 1];
    if (!manualSearch || (last && e.timeStamp - last > 1000)) keyTimesRef.current = [];
    keyTimesRef.current.push(e.timeStamp);
  };

  const showScanInput = showManualSearch || showQrInput;

  // Keep the single input ready for the next reader scan whenever the scan step is idle again.
  useEffect(() => {
    // Camera/face modes skip this so tablets do not pop the keyboard over the camera.
    if (currentStep !== 'scan' || showDynamicBadgePrint || showBadgePreviewModal || (showQrInput && scanMethod !== 'reader')) return undefined;
    const timer = setTimeout(() => manualSearchInputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [currentStep, showDynamicBadgePrint, showBadgePreviewModal, scanMethod, showQrInput]);
  const faceIdEnabled = event?.faceIdEnabled !== false;
  const activeScanMethod = !showQrInput || (scanMethod === 'face' && !faceIdEnabled) ? 'reader' : scanMethod;

  const handleFormDataChange = (updatedFormData) => {
    setCurrentFormData(updatedFormData);
  };

  const handleUserDataSave = (updatedFormData) => {
    if (!scannedUser?.userId) return;
    
    setIsUpdating(true);
    updateUserMutation.mutate({
      userId: scannedUser.userId,
      formData: updatedFormData
    });
  };

  const handleUserDataCancel = () => {
    setCurrentStep('scan');
    setScannedUser(null);
  };




  const handleBadgePreview = (registration) => {
    setSelectedRegistrationForBadge(registration);
    setShowBadgePreviewModal(true);
  };

  const handleOpenBadgeModal = () => {
    if (scannedUser) {
      handleBadgePreview(scannedUser);
    }
  };

  const handleNewScan = () => {
    setScannedUser(null);
    setCurrentStep('scan');
  };

  // Force refetch badge config when scannedUser changes (to get correct purpose)
  useEffect(() => {
    if (scannedUser?.purposeConfig?.purpose) {
      refetchBadgeConfig();
    }
  }, [scannedUser?.purposeConfig?.purpose, refetchBadgeConfig]);

  // Auto-refresh badge preview when form data changes (debounced)
  useEffect(() => {
    if (scannedUser && debouncedFormData) {
      const updatedBadgeData = {
        ...debouncedFormData,
        userId: scannedUser.userId,
        qrCodeBase64: scannedUser.qrCodeBase64,
        redeemedBy: scannedUser.redeemedBy,
        redeemedAt: formatDateTime(scannedUser.redeemedAt, ''),
        purpose: scannedUser.purpose
      };
      setBadgePreviewData(updatedBadgeData);
    }
  }, [debouncedFormData, scannedUser]);

  // Initialize badge preview data when scanned user changes
  useEffect(() => {
    if (scannedUser) {
      const initialBadgeData = {
        ...scannedUser.formData,
        userId: scannedUser.userId,
        qrCodeBase64: scannedUser.qrCodeBase64,
        redeemedBy: scannedUser.redeemedBy,
        redeemedAt: formatDateTime(scannedUser.redeemedAt, ''),
        purpose: scannedUser.purpose
      };
      setBadgePreviewData(initialBadgeData);
      setCurrentFormData(scannedUser.formData);
    } else {
      setBadgePreviewData(null);
    }
  }, [scannedUser]);

  useEffect(() => {
    const locked = lockedPurposeRef.current;
    if (!locked || purposeQueryParam) return;
    const params = new URLSearchParams(location.search);
    params.set('purpose', locked);
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
  }, [purposeQueryParam, location.pathname, location.search, navigate]);

  // Auto-scan when registrationId is provided in URL (skip step 1)
  useEffect(() => {
    if (registrationIdParam && !scannedUser && !isAutoScanning && allPurposes.length > 0
      && autoScannedIdRef.current !== registrationIdParam) {
      autoScannedIdRef.current = registrationIdParam;

      setIsAutoScanning(true);
      
      // Find the purpose config based on query parameter or default to first available
      const targetPurpose = purposeQueryParam 
        ? allPurposes.find(p => p.purpose === purposeQueryParam) || allPurposes[0]
        : allPurposes[0];
      
      if (targetPurpose) {

        
        // Create a mock scan result to simulate QR scan
        const mockScanResult = {
          userId: registrationIdParam,
          purpose: targetPurpose.purpose,
          purposeConfig: clonePurposeConfig(targetPurpose)
        };
        
        // Call handleQRScan directly to skip the scanning UI
        handleQRScan(mockScanResult);
      } else {
        setIsAutoScanning(false);
        showError('No scan purpose configuration found for this event');
      }
    }
  }, [registrationIdParam, scannedUser, isAutoScanning, allPurposes, purposeQueryParam, handleQRScan, showError]);

  // Global external scanner handler: catches a hardware scan when no input has focus.
  // The buffer lives in a ref because scanner keystrokes arrive faster than React re-renders.
  const scannerBufferRef = useRef('');
  const scanFromSearchRef = useRef(scanFromSearch);
  scanFromSearchRef.current = scanFromSearch;

  useEffect(() => {
    if (currentStep !== 'scan') return undefined;
    let resetTimer;
    const setBuffer = (value) => {
      scannerBufferRef.current = value;
      setExternalScannerInput(value);
    };

    const handleGlobalKeyPress = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || event.target?.isContentEditable) {
        return;
      }

      if (event.key === 'Enter') {
        const code = scannerBufferRef.current.trim();
        if (code) {
          event.preventDefault();
          setBuffer('');
          scanFromSearchRef.current(code);
        }
        return;
      }

      if (event.key === 'Backspace') {
        setBuffer(scannerBufferRef.current.slice(0, -1));
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        setBuffer(scannerBufferRef.current + event.key);
      } else {
        return;
      }

      // Drop a partial scan after 2s of inactivity.
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => setBuffer(''), 2000);
    };

    document.addEventListener('keydown', handleGlobalKeyPress);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyPress);
      clearTimeout(resetTimer);
    };
  }, [currentStep]);

  // Force refresh: Refetch both badge config and user data from backend
  const handleForceRefresh = async () => {
    if (!scannedUser?.userId) {
      showError('No user data to refresh');
      return;
    }

    setIsForceRefreshing(true);
    try {
      // Invalidate and refetch badge config for current purpose
      queryClient.invalidateQueries(['badgeDesignerConfig', eventId, scannedUser?.purpose || 'badge']);
      
      // Refetch user registration data from backend
      const freshUserData = await ApiService.getRegistration(eventId, scannedUser.userId);
      
      // Update scanned user with fresh data
      const updatedScannedUser = {
        ...scannedUser,
        formData: freshUserData.formData,
        ...freshUserData
      };
      
      setScannedUser(updatedScannedUser);
      setCurrentFormData(freshUserData.formData);
      
      showSuccess('Badge preview refreshed with latest data from server');
    } catch (error) {
      console.error('Force refresh failed:', error);
      showError('Failed to refresh data from server');
    } finally {
      setIsForceRefreshing(false);
    }
  };

  // Legacy refresh function (kept for compatibility)
  const handleRefreshBadgePreview = () => {
    handleForceRefresh();
  };

  if (eventLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h2>
        <p className="text-gray-600 mb-6">The event you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/admin')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div
      className={scanBackground ? 'min-h-screen flex flex-col justify-center bg-cover bg-center bg-fixed px-3 py-6 sm:px-6 sm:py-10' : undefined}
      style={scanBackground ? { backgroundImage: `url(${scanBackground})` } : undefined}
    >
    {/* With a background, the scanner sits in a centred card that grows with its content. */}
    <div className={scanBackground
      ? 'mx-auto w-full max-w-3xl space-y-4 sm:space-y-5 rounded-2xl bg-white/95 p-4 sm:p-6 shadow-2xl ring-1 ring-black/5 backdrop-blur'
      : 'space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6'}>
      {scanLogo && (
        <img
          src={scanLogo}
          alt={`${event.name} logo`}
          className="w-full max-h-40 object-contain rounded-lg"
          onError={() => setScanLogoError(true)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{event.name}</h1>
          <p className="text-sm sm:text-base text-gray-600">QR Scanner & Badge Management</p>
          {fromDesk && (
            <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate(deskPath(eventId))}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to desk
            </Button>
          )}
        </div>

        {currentStep === 'scan' && filteredPurposes.length > 0 && (
          <div className="flex flex-col gap-2 sm:items-end">
            <Select value={selectedPurpose || undefined} onValueChange={switchPurpose}>
              <SelectTrigger className="sm:w-56">
                <SelectValue placeholder="Select scan purpose" />
              </SelectTrigger>
              <SelectContent>
                {filteredPurposes.map((purpose) => (
                  <SelectItem key={purpose.purpose} value={purpose.purpose}>
                    {purpose.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ScannerNameChip />
            {canToggleAllPurposes && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <Switch checked={showAllPurposes} onCheckedChange={setShowAllPurposes} />
                Show all purposes
              </label>
            )}
          </div>
        )}
      </div>

      {/* Offline cache + background-sync status */}
      <OfflineScanStatus eventId={eventId} offlineEnabled={!!event?.offlineEnabled} />

      {/* Warning for invalid purpose parameter */}
      {purposeQueryParam && !isValidPurpose && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-sm text-yellow-800">
                <strong>Invalid purpose parameter:</strong> "{purposeQueryParam}" is not available for this event.
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Showing all available purposes instead.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`grid grid-cols-1 ${scannedUser?.purposeConfig?.shouldPrintBadge ? 'xl:grid-cols-1' : 'xl:grid-cols-1'} gap-4 sm:gap-6`}>
        {/* Left Column - Scanner/Editor */}
        <div className="space-y-4 sm:space-y-6">
          {currentStep === 'scan' && !awaitingAutoScan && (
            <>
              {selectedPurpose && event?.scanShowStats !== false && (
                <QRScanStats eventId={eventId} purpose={selectedPurpose} />
              )}

              {/* Result of the last search/scanner scan; shown even when the search box is hidden. */}
              {searchScanResult && (
                <div className={`flex items-start gap-3 rounded-lg border p-4 ${
                  searchScanResult.ok ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-500 text-red-900'
                }`}>
                  {searchScanResult.ok ? <CheckCircle className="w-5 h-5 mt-0.5" /> : <AlertTriangle className="w-5 h-5 mt-0.5" />}
                  <div className="text-sm space-y-0.5">
                    <p className="font-semibold">
                      {searchScanResult.ok ? (searchScanResult.name || searchScanResult.userId) : 'Scan failed'}
                    </p>
                    {searchScanResult.userId && <p className="font-mono text-xs">{searchScanResult.userId}</p>}
                    {searchScanResult.category && <p>{searchScanResult.category}</p>}
                    <p>{searchScanResult.message}</p>
                    {searchScanResult.previousScans && (
                      <p className="font-medium text-amber-800">{searchScanResult.previousScans}</p>
                    )}
                    <p className="text-xs opacity-70">{formatDateTime(searchScanResult.at)}</p>
                  </div>
                </div>
              )}

              {showScanInput && (
                <Card>
                  <CardContent className="space-y-3 pt-6">
                    <div className="flex flex-col sm:flex-row gap-2">
                      {showQrInput && (
                        <Select value={activeScanMethod} onValueChange={setScanMethod}>
                          <SelectTrigger className="sm:w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="reader">{showManualSearch ? 'Reader / search' : 'Scan with reader'}</SelectItem>
                            <SelectItem value="camera">Scan with camera</SelectItem>
                            {faceIdEnabled && <SelectItem value="face">Face recognition</SelectItem>}
                          </SelectContent>
                        </Select>
                      )}
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          ref={manualSearchInputRef}
                          placeholder={showManualSearch
                            ? 'Scan QR, or search by name, email, contact, registration ID'
                            : 'Scan QR code or enter registration ID'}
                          value={manualSearch}
                          onChange={(e) => setManualSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleManualSearchEnter();
                              return;
                            }
                            trackKeyTiming(e);
                          }}
                          autoFocus
                          autoComplete="off"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {activeScanMethod !== 'reader' && (
                      <QRScanner
                        eventId={eventId}
                        onScan={handleQRScan}
                        onError={(error) => showError(error)}
                        purposes={filteredPurposes}
                        preselectedPurpose={purposeQueryParam}
                        selectedPurpose={selectedPurpose || null}
                        scanMethod={activeScanMethod}
                        continuousMode={continuousScanMode}
                        // For this scan flow, always stop the camera after a successful scan.
                        autoStopAfterSuccess={true}
                        faceIdEnabled={faceIdEnabled}
                        className="p-0 border-0 shadow-none"
                      />
                    )}

                    {showManualSearch && debouncedManualSearch.trim().length > 0 && manualSearch.trim().length > 0 && (
                      <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
                        {isManualSearching ? (
                          <div className="p-4 text-sm text-gray-400">Searching…</div>
                        ) : manualSearchResults.length === 0 ? (
                          <div className="p-4 text-sm text-gray-400">No matching attendees found. Press Enter to scan it as a code.</div>
                        ) : (
                          manualSearchResults.map((reg) => {
                            const name = toTitleCase(getFormField(reg, ['Name', 'name', 'Full Name', 'fullName']));
                            const details = pickDisplayFields(reg.formData, searchResultFields);
                            return (
                              <div key={reg.userId} className="p-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{name || reg.userId}</p>
                                  <div className="text-xs text-gray-500 space-y-0.5 mt-0.5">
                                    <p className="font-mono">{reg.userId}</p>
                                    {details.map(({ label, value }) => {
                                      const Icon = searchFieldIcon(label);
                                      return (
                                        <p key={label} className="flex items-center gap-1 truncate">
                                          {Icon ? <Icon className="w-3 h-3 flex-shrink-0" /> : <span className="text-gray-400">{label}:</span>}
                                          {value}
                                        </p>
                                      );
                                    })}
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => scanFromSearch(reg.userId)}
                                  disabled={isSearchScanning || fetchRegistrationMutation.isPending}
                                  className="flex-shrink-0 bg-blue-600 hover:bg-blue-700"
                                >
                                  {targetPurpose?.shouldPrintBadge ? (
                                    <><Printer className="w-4 h-4 mr-1" />Print Badge</>
                                  ) : (
                                    <><CheckCircle className="w-4 h-4 mr-1" />{targetPurpose?.name || 'Select'}</>
                                  )}
                                </Button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* External Scanner Status */}
              {externalScannerInput && (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-yellow-800">
                        External scanner detected: <span className="font-mono">{externalScannerInput}</span>
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

            </>
          )}

          {currentStep === 'scan' && awaitingAutoScan && (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {autoScanParam ? 'Preparing Badge Print...' : 'Loading Registration Data...'}
                </h3>
                <p className="text-gray-600">
                  {autoScanParam ? 'Loading registration details for' : 'Processing registration ID:'} 
                  {' '}<span className="font-mono bg-gray-100 px-2 py-1 rounded">{registrationIdParam}</span>
                </p>
                {isAutoScanning && (
                  <p className="text-sm text-blue-600 mt-2">
                    {autoScanParam ? 'Skipping scan step - going directly to badge preview...' : 'Automatically scanning in background...'}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {currentStep === 'edit' && scannedUser && (
            <UserDetailsEditor
              userData={scannedUser}
              formSchema={formSchema}
              onSave={handleUserDataSave}
              onCancel={handleUserDataCancel}
              onChange={handleFormDataChange}
              isLoading={isUpdating}
              currentScanPurpose={scannedUser.purposeConfig?.purpose}
            />
          )}

          {currentStep === 'preview' && scannedUser && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Scan Complete</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <p className="text-sm text-green-800">
                      {scannedUser.purposeConfig?.canUpdateUser 
                        ? 'User details updated successfully!'
                        : 'User details verified successfully!'}
                    </p>
                  </div>
                </div>

                {/* User Details Section */}
                <div className="bg-white border rounded-lg divide-y">
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-2">User Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-500">User ID</label>
                        <p className="font-mono text-sm">{scannedUser.userId}</p>
                      </div>
                      {Object.entries(scannedUser.formData || {}).map(([key, value]) => (
                        <div key={key}>
                          <label className="text-sm text-gray-500">{key}</label>
                          <p className="font-medium">{value?.toString() || 'N/A'}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Purpose Information */}
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-2">Scan Details</h3>
                    <div className="space-y-2">
                      <div>
                        <label className="text-sm text-gray-500">Purpose</label>
                        <p className="font-medium">{scannedUser.purposeConfig?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Scan Time</label>
                        <p className="font-medium">{formatDateTime(new Date())}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button onClick={handleNewScan} variant="outline">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    New Scan
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Dynamic Badge Preview (only shown if shouldPrintBadge is true) */}
        {scannedUser?.purposeConfig?.shouldPrintBadge && (
          <div className="space-y-6">
            {!badgeConfig && !badgeConfigLoading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Badge Configuration Missing</h3>
                  <p className="text-gray-500 mb-4">
                    No badge configuration found for this event. Please configure the badge design first.
                  </p>
                  <div className="space-y-2">
                    <Button onClick={() => navigate(`/admin/events/${eventRef}/badge-config`)}>
                      Configure Badge
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={async () => {
                        try {
                          const testConfig = {
                            eventId: eventId,
                            paperSize: { width: 4, height: 6, name: "4\" × 6\"" },
                            fields: [
                              {
                                id: "test_name",
                                key: "Name",
                                label: "Name", 
                                type: "text",
                                x: 0.5,
                                y: 1,
                                width: 3,
                                height: 0.5,
                                visible: true,
                                style: {
                                  fontSize: 16,
                                  fontWeight: "bold",
                                  color: "#000000",
                                  fontFamily: "Arial, sans-serif",
                                  textAlign: "left"
                                }
                              }
                            ]
                          };
                          
                          const result = await ApiService.saveBadgeDesignerConfig(eventId, testConfig);
                          showSuccess('Test badge config created!');
                          
                          // Refetch badge config
                          setTimeout(() => {
                            window.location.reload();
                          }, 1000);
                        } catch (error) {
                          console.error('Failed to create test config:', error);
                          showError(`Failed to create test config: ${error.message}`);
                        }
                      }}
                    >
                      Create Test Badge Config  
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (badgeConfig && false) && (
              <Card className="shadow-lg">
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <CardTitle className="flex items-center space-x-2">
                      <Eye className="w-5 h-5" />
                      <span className="text-lg sm:text-xl">Live Badge Preview</span>
                    </CardTitle>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleForceRefresh}
                        disabled={badgeConfigLoading || isForceRefreshing}
                        className="flex items-center justify-center space-x-1 w-full sm:w-auto"
                      >
                        <RefreshCcw className={`w-4 h-4 ${isForceRefreshing ? 'animate-spin' : ''}`} />
                        <span className="text-xs sm:text-sm">{isForceRefreshing ? 'Refreshing...' : 'Force Refresh'}</span>
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => setShowDynamicBadgePrint(true)}
                        disabled={!badgeConfig}
                        className="flex items-center justify-center space-x-1 w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                      >
                        <Printer className="w-4 h-4" />
                        <span className="text-xs sm:text-sm">Print Badge</span>
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {badgeConfigLoading && (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      <span className="ml-2">Loading badge configuration...</span>
                    </div>
                  )}

                  {badgeConfig && !badgeConfigLoading && (
                    <div className="w-full">
                      {badgeConfig.backgroundImage && (
                        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-700">
                            ℹ️ Background image shown for alignment preview only - will not appear in printed badge
                          </p>
                        </div>
                      )}
                      
                      {/* Auto-refresh indicator */}
                      {currentFormData && JSON.stringify(currentFormData) !== JSON.stringify(debouncedFormData) && (
                        <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <RefreshCcw className="w-4 h-4 animate-spin text-green-600" />
                            <p className="text-sm text-green-700">
                              Auto-updating preview as you type...
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <DynamicBadgeRenderer
                        badgeConfig={badgeConfig}
                        userData={badgePreviewData}
                        showPreview={true}
                      />
                    </div>
                  )}

                  {badgeConfigError && (
                    <div className="text-center text-red-500 py-8">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
                      <p>Failed to load badge configuration</p>
                      <p className="text-sm mt-2">{badgeConfigError.message}</p>
                    </div>
                  )}

                  {badgeConfig && !badgeConfigLoading && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-800">
                        <strong>Live Preview:</strong> This badge is ready to print with the scanned user's data.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {currentStep === 'edit' && scannedUser && (
        <div className="flex justify-center space-x-4">
          <Button
            variant="outline"
            onClick={handleUserDataCancel}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setCurrentStep('preview')}
            disabled={isUpdating}
          >
            Continue to Preview
          </Button>
        </div>
      )}

      {isPrintingBadge && <PrintingInProgress onDone={backToDesk} />}

      {/* Badge Preview Modal */}
      <BadgePreviewModal
        eventId={eventId}
        registrationId={selectedRegistrationForBadge?.userId}
        isOpen={showBadgePreviewModal}
        onClose={() => {
          setShowBadgePreviewModal(false);
          setSelectedRegistrationForBadge(null);
        }}
        registrationData={selectedRegistrationForBadge?.formData}
      />

      {/* Dynamic Badge Print Modal */}
      {scannedUser && badgeConfig && badgePreviewData && (
        <DynamicBadgePrintModal
          isOpen={showDynamicBadgePrint}
          onClose={() => {
            setShowDynamicBadgePrint(false);
            if (registrationIdParam) {
              clearAutoScanParams();
              setScannedUser(null);
            }
          }}
          badgeConfig={badgeConfig}
          userData={badgePreviewData}
          eventId={eventId}
          scannedUser={scannedUser}
          formSchema={formSchema}
          lockerEnabled={!!event?.lockerEnabled}
          onPrintStarted={handleBadgePrintStarted}
          shareEnabled={badgeShareAllowed(event)}
          onUserDataUpdate={async (userId, formData) => {
            // Update user data via API
            await ApiService.updateRegistration(eventId, userId, formData);
            // Update local state
            const updatedScannedUser = {
              ...scannedUser,
              formData: formData
            };
            setScannedUser(updatedScannedUser);
            setCurrentFormData(formData);
            // Update badge preview data
            setBadgePreviewData({
              ...badgePreviewData,
              ...formData
            });
            // Invalidate queries to refresh data
            queryClient.invalidateQueries(['eventRegistrations', eventId]);
          }}
        />
      )}
    </div>
    </div>
  );
};

export default QRScanFlowPage;
